import { apiClient } from '@/api/client'
import type {
  AiTemplateRequest,
  AiTemplateResponse,
  Equipment,
  Exercise,
  ExerciseCreateInput,
  ExerciseHistoryPoint,
  ExerciseLatestStats,
  ExerciseUpdateInput,
  MuscleSummary,
  ProfileRead,
  ProfileUpdateInput,
  SessionStartInput,
  SessionUpdateInput,
  SetCreateInput,
  SetUpdateInput,
  TemplateCreateInput,
  TemplateUpdateInput,
  WorkoutSession,
  WorkoutSessionHistoryPage,
  WorkoutSessionListItem,
  WorkoutSetRecord,
  WorkoutTemplate,
} from '@/api/types/workout'

// Exercises
export const getExercises = async (includeArchived = false): Promise<Exercise[]> => {
  const res = await apiClient.get(`/workout/exercises`, { params: { include_archived: includeArchived } })
  return res.data
}

export const createExercise = async (payload: ExerciseCreateInput): Promise<Exercise> => {
  const res = await apiClient.post(`/workout/exercises`, payload)
  return res.data
}

export const updateExercise = async (exerciseId: number, payload: ExerciseUpdateInput): Promise<Exercise> => {
  const res = await apiClient.patch(`/workout/exercises/${exerciseId}`, payload)
  return res.data
}

export const archiveExercise = async (exerciseId: number): Promise<void> => {
  await apiClient.delete(`/workout/exercises/${exerciseId}`)
}

export const getExerciseHistory = async (exerciseId: number, limit = 20): Promise<ExerciseHistoryPoint[]> => {
  const res = await apiClient.get(`/workout/exercises/${exerciseId}/history`, { params: { limit } })
  return res.data
}

export const getExerciseLatestStats = async (): Promise<ExerciseLatestStats[]> => {
  const res = await apiClient.get(`/workout/exercises/latest-stats`)
  return res.data
}

/** 筋群ごとの負荷。sessionId を渡すとそのセッションだけ、無ければ直近 days 日の完了済みセッション */
export const getMuscleSummary = async (params: { days?: 7 | 30; sessionId?: number }): Promise<MuscleSummary> => {
  const res = await apiClient.get(`/workout/muscle-summary`, {
    params: { days: params.days, session_id: params.sessionId },
  })
  return res.data
}

// Templates
export const getTemplates = async (includeArchived = false): Promise<WorkoutTemplate[]> => {
  const res = await apiClient.get(`/workout/templates`, { params: { include_archived: includeArchived } })
  return res.data
}

export const createTemplate = async (payload: TemplateCreateInput): Promise<WorkoutTemplate> => {
  const res = await apiClient.post(`/workout/templates`, payload)
  return res.data
}

export const updateTemplate = async (templateId: number, payload: TemplateUpdateInput): Promise<WorkoutTemplate> => {
  const res = await apiClient.patch(`/workout/templates/${templateId}`, payload)
  return res.data
}

export const archiveTemplate = async (templateId: number): Promise<void> => {
  await apiClient.delete(`/workout/templates/${templateId}`)
}

// Sessions
export const getSessions = async (limit = 30): Promise<WorkoutSessionListItem[]> => {
  const res = await apiClient.get(`/workout/sessions`, { params: { limit } })
  return res.data
}

export const getSessionHistory = async (
  before: string | null,
  days: number,
  signal?: AbortSignal,
): Promise<WorkoutSessionHistoryPage> => {
  const res = await apiClient.get(`/workout/sessions/history`, {
    params: { before: before ?? undefined, days },
    signal,
  })
  return res.data
}

export const getActiveSession = async (): Promise<WorkoutSession | null> => {
  const res = await apiClient.get(`/workout/sessions/active`)
  return res.data
}

export const startSession = async (payload: SessionStartInput): Promise<WorkoutSession> => {
  const res = await apiClient.post(`/workout/sessions`, payload)
  return res.data
}

export const getSession = async (sessionId: number): Promise<WorkoutSession> => {
  const res = await apiClient.get(`/workout/sessions/${sessionId}`)
  return res.data
}

export const updateSession = async (sessionId: number, payload: SessionUpdateInput): Promise<WorkoutSession> => {
  const res = await apiClient.patch(`/workout/sessions/${sessionId}`, payload)
  return res.data
}

export const deleteSession = async (sessionId: number): Promise<void> => {
  await apiClient.delete(`/workout/sessions/${sessionId}`)
}

// Sets
export const addSet = async (sessionId: number, payload: SetCreateInput): Promise<WorkoutSetRecord> => {
  const res = await apiClient.post(`/workout/sessions/${sessionId}/sets`, payload)
  return res.data
}

export const updateSet = async (
  sessionId: number,
  setId: number,
  payload: SetUpdateInput,
): Promise<WorkoutSetRecord> => {
  const res = await apiClient.patch(`/workout/sessions/${sessionId}/sets/${setId}`, payload)
  return res.data
}

export const deleteSet = async (sessionId: number, setId: number): Promise<void> => {
  await apiClient.delete(`/workout/sessions/${sessionId}/sets/${setId}`)
}

// AI Evaluation
export const evaluateSession = async (sessionId: number): Promise<WorkoutSession> => {
  const res = await apiClient.post(`/workout/sessions/${sessionId}/evaluate`)
  return res.data
}

// Equipments
export const getEquipments = async (): Promise<Equipment[]> => {
  const res = await apiClient.get(`/workout/equipments`)
  return res.data
}

// Profile
export const getProfile = async (): Promise<ProfileRead> => {
  const res = await apiClient.get(`/workout/profile`)
  return res.data
}

export const updateProfile = async (payload: ProfileUpdateInput): Promise<ProfileRead> => {
  const res = await apiClient.put(`/workout/profile`, payload)
  return res.data
}

// AI Template Proposal
export const proposeTemplates = async (payload: AiTemplateRequest): Promise<AiTemplateResponse> => {
  const res = await apiClient.post(`/workout/templates/ai-propose`, payload)
  return res.data
}
